/**
 * @file meetingNameHighlight.tsx
 * @brief Participants' names marked with a dim highlighter colour, each
 *        person at their FIRST mention only. Used by the Overview alone.
 *
 * @details
 * ⏳ TRIAL (DES 08-10-2026, phone parity): one colour per participant in
 * speaking order, normal weight, behind the name like a highlighter. Only the
 * Overview passes names; Summary, Key decisions and the rest stay plain.
 * `MEETING_NAME_EMPHASIS` is the one switch; false = plain text.
 *
 * Phone source: FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/
 * components/MeetingIntelCopyText.tsx (`participantNameTerms`,
 * `splitByNames`, `renderRuns`, `highlighterColor`, the two palettes).
 *
 * Dark mode: each mark carries its light and dark colour as CSS variables and
 * Tailwind's `dark:` (class strategy) picks one, so no theme hook is needed.
 *
 * @author DES
 * @date 08-10-2026
 */

import type { CSSProperties, ReactNode } from "react";

/** The trial switch. false = plain text. */
export const MEETING_NAME_EMPHASIS = true;

/** Highlighter hues, one per participant in speaking order (RGB). */
const HIGHLIGHTER_RGB = [
  "255,204,0", // yellow
  "52,199,89", // green
  "0,149,255", // blue
  "255,45,146", // pink
  "255,149,0", // orange
  "175,82,222", // purple
];
/** Dark's own set: yellow, green and orange go murky on the dark page. */
const HIGHLIGHTER_RGB_DARK = [
  "10,132,255", // blue
  "191,90,242", // purple
  "255,55,95", // pink
  "100,210,255", // teal
  "94,92,230", // indigo
  "102,212,207", // mint
];
/** Dim: enough to mark the name, not enough to shout. */
const HIGHLIGHTER_ALPHA_LIGHT = 0.22;
const HIGHLIGHTER_ALPHA_DARK = 0.3;

export function highlighterColor(index: number, isDark: boolean): string {
  const set = isDark ? HIGHLIGHTER_RGB_DARK : HIGHLIGHTER_RGB;
  const rgb = set[index % set.length];
  const a = isDark ? HIGHLIGHTER_ALPHA_DARK : HIGHLIGHTER_ALPHA_LIGHT;
  return `rgba(${rgb},${a})`;
}

/** Labels that are not people ("Speaker 2", "Person A", "Unknown"). */
const NOT_A_NAME = /^(speaker|person|unknown|participant|guest)\b/i;

/** A string to mark, and whose colour it takes. */
type NameTerm = { term: string; person: number };

/**
 * The strings to mark: each full name (any "(Client)" style role dropped)
 * and each of its words of 3+ letters. Longest first, so a full name wins
 * over its first word. A word two people share keeps the first one's colour.
 */
export function participantNameTerms(names: string[]): NameTerm[] {
  const terms = new Map<string, number>();
  let person = 0;
  for (const raw of names) {
    const name = String(raw ?? "")
      .replace(/\(.*?\)/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!name || NOT_A_NAME.test(name)) continue;
    const add = (t: string) => {
      const k = t.toLowerCase();
      if (!terms.has(k)) terms.set(k, person);
    };
    add(name);
    for (const word of name.split(" ")) {
      if (word.length >= 3) add(word);
    }
    person += 1;
  }
  return Array.from(terms, ([term, p]) => ({ term, person: p })).sort(
    (a, b) => b.term.length - a.term.length,
  );
}

const isLetter = (ch: string | undefined) => !!ch && /[\p{L}\p{M}]/u.test(ch);

/** `text` cut into plain runs and name runs (whole words only). */
function splitByNames(text: string, terms: NameTerm[]): { text: string; person: number | null }[] {
  if (terms.length === 0) return [{ text, person: null }];
  const byTerm = new Map(terms.map((t) => [t.term, t.person]));
  const escaped = terms.map((t) => t.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "giu");
  const out: { text: string; person: number | null }[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    // Whole words only: "Karen" in "Karen's", not "Ann" in "Annual".
    if (isLetter(text[start - 1]) || isLetter(text[end])) {
      re.lastIndex = start + 1;
      continue;
    }
    if (start > last) out.push({ text: text.slice(last, start), person: null });
    out.push({ text: m[0], person: byTerm.get(m[0].toLowerCase()) ?? 0 });
    last = end;
  }
  if (last < text.length) out.push({ text: text.slice(last), person: null });
  return out;
}

/**
 * The text with each person marked once, at their first mention. `seen`
 * carries who is done, so a caller rendering several chunks of one text can
 * share it.
 */
export function renderNameHighlights(
  text: string,
  terms: NameTerm[],
  seen: Set<number> = new Set(),
): ReactNode {
  if (!MEETING_NAME_EMPHASIS || terms.length === 0 || !text) return text;
  const runs = splitByNames(text, terms).map((r) => {
    if (r.person == null) return r;
    if (seen.has(r.person)) return { ...r, person: null };
    seen.add(r.person);
    return r;
  });
  if (runs.every((r) => r.person == null)) return text;
  return runs.map((r, i) =>
    r.person != null ? (
      <span
        key={i}
        className="rounded-[3px] bg-[var(--hl-light)] dark:bg-[var(--hl-dark)]"
        style={
          {
            "--hl-light": highlighterColor(r.person, false),
            "--hl-dark": highlighterColor(r.person, true),
          } as CSSProperties
        }
      >
        {r.text}
      </span>
    ) : (
      r.text
    ),
  );
}

/**
 * The meeting review's body line height (Overview, Summary): 26 → 28px at
 * 16px for Latin, the phone's 22 → 24 at 17pt (×24/22, DES 08-10-2026).
 * Right-to-left copy keeps its own 26px, as the phone's native scripts do.
 */
export function meetingBodyLeading(rtl: boolean): string {
  return rtl ? "leading-[26px]" : "leading-[28px]";
}
